import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { currency, UNITS } from "@/lib/portalConstants";
import { format } from "date-fns";
import { Plus, X, ClipboardCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const emptyInvoice = () => ({
  client_id: "",
  issue_date: format(new Date(), "yyyy-MM-dd"),
  due_date: "",
  line_items: [],
  tax_rate: 0,
  notes: "",
  status: "draft",
  care_log_ids: [],
});

const fmtVisitDate = (s) =>
  new Date(`${s}T00:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

const visitServices = (l) =>
  [...(l.personal_care || []), ...(l.assisted_with || []), ...(l.housekeeping || [])];

export function InvoiceDialog({ open, onOpenChange, clients, services, editInvoice, initialClientId, onSaved }) {
  const [form, setForm] = useState(emptyInvoice());
  const [saving, setSaving] = useState(false);
  const [allLogs, setAllLogs] = useState([]);
  const [checkedVisits, setCheckedVisits] = useState([]);
  const [addedVisitIds, setAddedVisitIds] = useState([]);
  const [visitRate, setVisitRate] = useState("");

  // Her rate for the selected client (falls back to the hourly service catalog rate)
  const rateFor = (clientId) => {
    const cl = clients.find((c) => c.id === clientId);
    if (cl?.hourly_rate > 0) return cl.hourly_rate;
    return services.find((s) => s.unit === "hour")?.rate ?? "";
  };

  useEffect(() => {
    if (open) {
      setForm(
        editInvoice
          ? {
              client_id: editInvoice.client_id,
              issue_date: editInvoice.issue_date,
              due_date: editInvoice.due_date || "",
              line_items: editInvoice.line_items.map((li) => ({ description: li.description, quantity: li.quantity, unit: li.unit, rate: li.rate })),
              tax_rate: editInvoice.tax_rate || 0,
              notes: editInvoice.notes || "",
              status: editInvoice.status,
              care_log_ids: editInvoice.care_log_ids || [],
            }
          : { ...emptyInvoice(), client_id: initialClientId || clients[0]?.id || "" }
      );
      setCheckedVisits([]);
      setAddedVisitIds(editInvoice?.care_log_ids || []);
      setVisitRate(rateFor(initialClientId || clients[0]?.id || ""));
      api.get("/portal/care-logs").then((r) => setAllLogs(r.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editInvoice, initialClientId, clients, services]);

  // Visits for this client that haven't been billed yet (and have hours recorded)
  const availableVisits = allLogs.filter(
    (l) => l.client_id === form.client_id && !l.invoiced && l.hours > 0 && !addedVisitIds.includes(l.id)
  );

  const toggleVisit = (id) =>
    setCheckedVisits((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const addCheckedVisits = () => {
    const rate = parseFloat(visitRate);
    if (!(rate > 0)) return toast.error("Set an hourly rate for these visits.");
    const chosen = availableVisits.filter((l) => checkedVisits.includes(l.id));
    const lines = chosen.map((l) => {
      const svc = visitServices(l);
      return {
        description: `In-home care — ${fmtVisitDate(l.date)}${svc.length ? `: ${svc.join(", ")}` : ""}`,
        quantity: l.hours,
        unit: "hour",
        rate,
        log_id: l.id,
      };
    });
    setForm((f) => ({
      ...f,
      line_items: [...f.line_items, ...lines],
      care_log_ids: [...(f.care_log_ids || []), ...chosen.map((l) => l.id)],
    }));
    setAddedVisitIds((s) => [...s, ...chosen.map((l) => l.id)]);
    setCheckedVisits([]);
    toast.success(`Added ${lines.length} ${lines.length === 1 ? "visit" : "visits"} to the invoice`);
  };

  const addLine = (svc) => {
    const line = svc
      ? { description: svc.name, quantity: 1, unit: svc.unit, rate: svc.rate }
      : { description: "", quantity: 1, unit: "hour", rate: 0 };
    setForm((f) => ({ ...f, line_items: [...f.line_items, line] }));
  };
  const updateLine = (idx, key, value) => {
    setForm((f) => {
      const items = [...f.line_items];
      items[idx] = { ...items[idx], [key]: value };
      return { ...f, line_items: items };
    });
  };
  const removeLine = (idx) =>
    setForm((f) => {
      const removed = f.line_items[idx];
      return {
        ...f,
        line_items: f.line_items.filter((_, i) => i !== idx),
        care_log_ids: removed?.log_id ? (f.care_log_ids || []).filter((id) => id !== removed.log_id) : f.care_log_ids,
      };
    });
  // removing a visit line makes the visit billable again (until the invoice is saved)
  const removeLineAndFree = (idx) => {
    const removed = form.line_items[idx];
    if (removed?.log_id && !editInvoice) setAddedVisitIds((s) => s.filter((id) => id !== removed.log_id));
    removeLine(idx);
  };

  const subtotal = form.line_items.reduce((s, li) => s + (parseFloat(li.quantity) || 0) * (parseFloat(li.rate) || 0), 0);
  const taxAmount = subtotal * ((parseFloat(form.tax_rate) || 0) / 100);
  const total = subtotal + taxAmount;

  const save = async () => {
    if (!form.client_id) return toast.error("Please choose a client.");
    if (form.line_items.length === 0) return toast.error("Add at least one line item.");
    const payload = {
      ...form,
      tax_rate: parseFloat(form.tax_rate) || 0,
      care_log_ids: form.care_log_ids || [],
      line_items: form.line_items.map(({ log_id, ...li }) => ({
        description: li.description,
        quantity: parseFloat(li.quantity) || 0,
        unit: li.unit,
        rate: parseFloat(li.rate) || 0,
      })),
    };
    setSaving(true);
    try {
      if (editInvoice) await api.put(`/portal/invoices/${editInvoice.id}`, payload);
      else await api.post("/portal/invoices", payload);
      toast.success(editInvoice ? "Invoice updated" : "Invoice created — those visits are now marked as billed");
      onOpenChange(false);
      onSaved?.();
    } catch {
      toast.error("Could not save invoice.");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-sand max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{editInvoice ? "Edit Invoice" : "New Invoice"}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label>Client</Label>
              <Select value={form.client_id} onValueChange={(v) => { setForm({ ...form, client_id: v }); if (!editInvoice) { setVisitRate(rateFor(v)); setCheckedVisits([]); } }}>
                <SelectTrigger data-testid="invoice-client" className="mt-1.5 bg-white"><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Issue date</Label><Input type="date" data-testid="invoice-issue" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className="mt-1.5 bg-white" /></div>
            <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1.5 bg-white" /></div>
          </div>

          {/* Bill from care visits */}
          {!editInvoice && availableVisits.length > 0 && (
            <div className="bg-sage/5 border border-sage/20 rounded-2xl p-4" data-testid="visit-picker">
              <p className="text-sm font-medium text-charcoal flex items-center gap-2 mb-1">
                <ClipboardCheck className="h-4 w-4 text-sage-700" /> Bill from care visits
              </p>
              <p className="text-xs text-charcoal-soft mb-3">Tick the visits you're billing for — the care log fills in the details.</p>
              <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
                {availableVisits.map((l) => (
                  <label key={l.id} data-testid={`visit-check-${l.id}`} className="flex items-center gap-3 bg-white rounded-xl border border-charcoal/10 px-3.5 py-2.5 cursor-pointer hover:border-sage/40 transition-colors">
                    <input type="checkbox" checked={checkedVisits.includes(l.id)} onChange={() => toggleVisit(l.id)} className="h-4 w-4 accent-sage shrink-0" />
                    <span className="text-sm text-charcoal font-medium shrink-0">{fmtVisitDate(l.date)}</span>
                    <span className="text-xs text-sage-700 font-medium shrink-0">{l.hours} hrs</span>
                    <span className="text-xs text-charcoal-soft truncate">{visitServices(l).join(", ") || "Care visit"}</span>
                  </label>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-3">
                <span className="text-xs text-charcoal-soft shrink-0">Hourly rate $</span>
                <Input type="number" min="0" step="0.01" value={visitRate} onChange={(e) => setVisitRate(e.target.value)} data-testid="visit-rate" className="h-10 w-24 bg-white" />
                <button
                  onClick={addCheckedVisits}
                  disabled={checkedVisits.length === 0}
                  data-testid="add-visits-button"
                  className="flex-1 rounded-full bg-sage text-white py-2.5 text-sm font-medium hover:bg-sage-dark transition-colors disabled:opacity-50"
                >
                  Add {checkedVisits.length || ""} {checkedVisits.length === 1 ? "visit" : "visits"} to invoice
                </button>
              </div>
            </div>
          )}

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Line items</Label>
              <div className="flex items-center gap-2">
                {services.length > 0 && (
                  <Select value="" onValueChange={(id) => { const svc = services.find((s) => s.id === id); if (svc) addLine(svc); }}>
                    <SelectTrigger data-testid="add-from-service" className="h-9 w-44 bg-white text-xs"><SelectValue placeholder="+ From service" /></SelectTrigger>
                    <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {currency(s.rate)}/{s.unit}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                <button onClick={() => addLine(null)} data-testid="add-line" className="text-xs text-sage-700 hover:underline flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Blank line</button>
              </div>
            </div>

            {form.line_items.length === 0 ? (
              <p className="text-sm text-charcoal/40 bg-white rounded-xl p-4 text-center">No line items yet — tick visits above, add from a service, or a blank line.</p>
            ) : (
              <div className="space-y-2">
                {form.line_items.map((li, idx) => (
                  <div key={idx} data-testid={`line-${idx}`} className="grid grid-cols-12 gap-2 items-center bg-white rounded-xl p-2">
                    <Input value={li.description} onChange={(e) => updateLine(idx, "description", e.target.value)} placeholder="Description" className="col-span-5 border-0 bg-transparent focus-visible:ring-0" data-testid={`line-desc-${idx}`} />
                    <Input type="number" min="0" step="0.5" value={li.quantity} onChange={(e) => updateLine(idx, "quantity", e.target.value)} className="col-span-2 border-0 bg-sand text-center" data-testid={`line-qty-${idx}`} />
                    <Select value={li.unit} onValueChange={(v) => updateLine(idx, "unit", v)}>
                      <SelectTrigger className="col-span-2 h-9 bg-sand text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input type="number" min="0" step="0.01" value={li.rate} onChange={(e) => updateLine(idx, "rate", e.target.value)} className="col-span-2 border-0 bg-sand text-right" data-testid={`line-rate-${idx}`} />
                    <button onClick={() => removeLineAndFree(idx)} className="col-span-1 text-charcoal/40 hover:text-destructive flex justify-center"><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 bg-white resize-none" rows={3} placeholder="A short thank-you note…" />
            </div>
            <div className="bg-white rounded-xl p-4 space-y-2">
              <div className="flex justify-between text-sm"><span className="text-charcoal-soft">Subtotal</span><span>{currency(subtotal)}</span></div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-charcoal-soft">Tax %</span>
                <Input type="number" min="0" step="0.1" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} className="h-8 w-20 bg-sand text-right" data-testid="invoice-tax" />
              </div>
              <div className="flex justify-between font-serif text-2xl text-charcoal border-t border-charcoal/10 pt-2" data-testid="invoice-total"><span>Total</span><span>{currency(total)}</span></div>
            </div>
          </div>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-invoice" className="mt-2 w-full rounded-full bg-sage text-white py-3 font-medium hover:bg-sage-dark transition-colors disabled:opacity-60">
          {saving ? "Saving…" : editInvoice ? "Save Changes" : "Create Invoice"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
